import { Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { SITE } from "../data/site";
import { LogoMark } from "./Logo";
import { useT } from "../i18n/LanguageContext";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.86 4.86 0 0 1-1.84-.31z"/>
  </svg>
);

export const Footer = () => {
  const { t } = useT();
  return (
    <footer data-testid="site-footer" className="bg-[var(--espresso)] text-[var(--ivory)]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <div className="bg-[var(--ivory)] rounded-full p-1">
                <LogoMark size={56} />
              </div>
              <div>
                <div className="font-display text-2xl">Pizzeria da Sofia</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-soft)]">Novara · Italia</div>
              </div>
            </div>
            <p className="mt-5 text-[var(--gold-soft)]/80 text-sm leading-relaxed max-w-sm">{t("footer.tagline")}</p>

            <div className="mt-6 flex items-center gap-3">
              <SocialBtn href={SITE.socials.instagram} label="Instagram"><Instagram className="w-4 h-4" /></SocialBtn>
              <SocialBtn href={SITE.socials.facebook} label="Facebook"><Facebook className="w-4 h-4" /></SocialBtn>
              <SocialBtn href={SITE.socials.tiktok} label="TikTok"><TikTokIcon className="w-4 h-4" /></SocialBtn>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-soft)] mb-4">{t("footer.contacts")}</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[var(--gold-soft)]" /><span>{SITE.address}<br/>{SITE.city}</span></li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[var(--gold-soft)]" /><a href={`tel:${SITE.phoneIntl}`} className="hover:text-[var(--gold-soft)]">{SITE.phone}</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-soft)] mb-4">{t("footer.hours")}</div>
            <ul className="space-y-1.5 text-sm">
              {SITE.hours.map((h) => (
                <li key={h.dayKey} className={`flex justify-between gap-4 ${h.closed ? "text-[var(--gold-soft)] italic" : "text-[var(--gold-soft)]/85"}`}>
                  <span>{t(`days.${h.dayKey}`)}</span>
                  <span>{h.closed ? t("footer.closed") : h.value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-soft)] mb-4">{t("footer.explore")}</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#about" className="hover:text-[var(--gold-soft)]">{t("nav.storia")}</a></li>
              <li><a href="#menu" className="hover:text-[var(--gold-soft)]">{t("nav.menu")}</a></li>
              <li><a href="#recensioni" className="hover:text-[var(--gold-soft)]">{t("nav.recensioni")}</a></li>
              <li><a href="#galleria" className="hover:text-[var(--gold-soft)]">{t("nav.galleria")}</a></li>
              <li><a href="#mappa" className="hover:text-[var(--gold-soft)]">{t("nav.doveSiamo")}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[var(--gold-soft)]/15 flex flex-col md:flex-row gap-3 justify-between text-[11px] text-[var(--gold-soft)]/60">
          <div>© {new Date().getFullYear()} Pizzeria da Sofia · {t("footer.rights")}</div>
          <div>{t("footer.siteCredit")}</div>
        </div>
      </div>
    </footer>
  );
};

const SocialBtn = ({ href, label, children }) => (
  <a
    href={href}
    aria-label={label}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`footer-social-${label.toLowerCase()}`}
    className="w-10 h-10 inline-flex items-center justify-center border border-[var(--gold-soft)]/30 rounded-full hover:bg-[var(--gold)] hover:border-[var(--gold)] hover:text-[var(--espresso)] transition-colors"
  >
    {children}
  </a>
);
